import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="composer-autosize"
//
// Two responsibilities:
//   1. Auto-grow the textarea between minRows and maxRows as the user types.
//   2. Submit the form on Cmd/Ctrl+Enter (Enter alone inserts a newline).
//
// Listens at document level for "ai-chip:clicked" CustomEvents (dispatched
// by the ai-chips controller). The chips live in a sibling partial that
// doesn't share a parent controller with the form, so a document-level
// pub/sub keeps the wiring loose.
//
// Targets:
//   textarea — the message textarea
//   form     — the surrounding form (defaults to controller's element)
//
// Values:
//   minRows  — starting/min row count (default 1)
//   maxRows  — max row count before scroll (default 6)
export default class extends Controller {
  static targets = ["textarea", "form"]
  static values = { minRows: { type: Number, default: 1 }, maxRows: { type: Number, default: 6 } }

  connect() {
    this.boundResize = this.resize.bind(this)
    this.boundKeydown = this.handleKeydown.bind(this)
    this.boundChip = this.handleChip.bind(this)
    this.textareaTarget.addEventListener("input", this.boundResize)
    this.textareaTarget.addEventListener("keydown", this.boundKeydown)
    document.addEventListener("ai-chip:clicked", this.boundChip)
    // Initial sizing in case the textarea has pre-filled content.
    this.resize()
  }

  disconnect() {
    this.textareaTarget.removeEventListener("input", this.boundResize)
    this.textareaTarget.removeEventListener("keydown", this.boundKeydown)
    document.removeEventListener("ai-chip:clicked", this.boundChip)
  }

  resize() {
    const ta = this.textareaTarget
    const lineHeight = parseFloat(getComputedStyle(ta).lineHeight) || 20
    ta.rows = this.minRowsValue
    const maxPx = lineHeight * this.maxRowsValue
    const needed = Math.min(ta.scrollHeight, maxPx)
    const rows = Math.max(this.minRowsValue, Math.ceil(needed / lineHeight))
    ta.rows = rows
    ta.style.overflowY = ta.scrollHeight > maxPx ? "auto" : "hidden"
  }

  handleKeydown(event) {
    // Cmd+Enter (mac) or Ctrl+Enter (other) submits.
    if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
      event.preventDefault()
      this.submit()
    }
  }

  submit() {
    const form = this.hasFormTarget ? this.formTarget : this.element.closest("form")
    if (!form) return
    if (typeof form.requestSubmit === "function") {
      form.requestSubmit()
    } else {
      form.submit()
    }
  }

  // Receives the chip click via the ai-chips controller's CustomEvent.
  // event.detail.prompt is the starter text the user can edit before sending.
  handleChip(event) {
    const prompt = event.detail?.prompt
    if (!prompt) return
    this.textareaTarget.value = prompt
    this.textareaTarget.focus()
    const len = this.textareaTarget.value.length
    this.textareaTarget.setSelectionRange(len, len)
    this.resize()
  }
}
